<template>
  <div class="whatsapp-template-selector">
    <div class="mb-6">
      <div class="flex items-center justify-between">
        <div>
          <h4 class="text-lg font-medium text-slate-900 dark:text-white mb-2">
            Selecciona una plantilla de WhatsApp
          </h4>
          <p class="text-sm text-slate-600 dark:text-slate-400">
            Elige la plantilla que deseas usar para tu campaña masiva
          </p>
        </div>
        <button
          v-if="selectedInbox && !isLoading"
          @click="syncTemplates"
          :disabled="isSyncing"
          class="flex items-center px-3 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 border border-blue-300 dark:border-blue-700 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Icon 
            :icon="isSyncing ? 'i-lucide-loader-2' : 'i-lucide-refresh-cw'" 
            :class="{ 'animate-spin': isSyncing }" 
            size="16" 
            class="mr-2" 
          />
          {{ isSyncing ? 'Sincronizando...' : 'Actualizar plantillas' }}
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <div class="text-center">
        <Icon icon="i-lucide-loader-2" class="animate-spin text-slate-400 mb-3" size="32" />
        <p class="text-sm text-slate-600 dark:text-slate-400">
          Cargando plantillas...
        </p>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
      <div class="flex items-start space-x-3">
        <Icon icon="i-lucide-alert-circle" class="text-red-500 mt-0.5" size="20" />
        <div>
          <h6 class="font-medium text-red-900 dark:text-red-100">
            Error al cargar plantillas
          </h6>
          <p class="mt-1 text-sm text-red-700 dark:text-red-300">
            {{ error }}
          </p>
          <button
            @click="loadTemplates"
            class="mt-2 px-3 py-1 text-sm font-medium text-red-700 dark:text-red-300 border border-red-300 dark:border-red-700 rounded hover:bg-red-100 dark:hover:bg-red-900/30"
          >
            Reintentar
          </button>
        </div>
      </div>
    </div>

    <!-- Templates List -->
    <div v-else class="space-y-4">
      <!-- Search/Filter -->
      <div class="flex items-center space-x-4">
        <div class="flex-1 relative">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Buscar plantillas por nombre..."
            class="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          />
        </div>
        <div class="w-40">
          <select
            v-model="languageFilter"
            class="w-full px-3 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          >
            <option value="">Idiomas</option>
            <option v-for="lang in availableLanguages" :key="lang" :value="lang">
              {{ getLanguageName(lang) }}
            </option>
          </select>
        </div>
      </div>

      <!-- Templates Grid -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div
          v-for="template in filteredTemplates"
          :key="template.id"
          @click="selectTemplate(template)"
          :class="[
            'p-4 border rounded-lg cursor-pointer transition-all group',
            selectedTemplate?.id === template.id
              ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
              : 'border-slate-200 dark:border-slate-700 hover:border-green-300 hover:bg-green-50/50 dark:hover:bg-green-900/10'
          ]"
        >
          <!-- Template Header -->
          <div class="flex items-start justify-between mb-3">
            <div>
              <h5
                :class="[
                  'font-medium',
                  selectedTemplate?.id === template.id
                    ? 'text-green-900 dark:text-green-100'
                    : 'text-slate-900 dark:text-white'
                ]"
              >
                {{ template.name }}
              </h5>
              <div class="flex items-center flex-wrap gap-1 mt-1">
                <span
                  :class="[
                    'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium',
                    template.status === 'APPROVED'
                      ? 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300'
                      : 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300'
                  ]"
                >
                  {{ template.status }}
                </span>
                <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300">
                  {{ getLanguageName(template.language) }}
                </span>
                <span v-if="template.inbox_name" class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400">
                  {{ template.inbox_name }}
                </span>
                <span v-if="hasMultimedia(template)" class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-300">
                  <Icon icon="i-lucide-image" size="12" class="mr-1" />
                  Multimedia
                </span>
              </div>
            </div>
            <Icon
              v-if="selectedTemplate?.id === template.id"
              icon="i-lucide-check-circle"
              class="text-green-500"
              size="20"
            />
          </div>

          <!-- Template Preview -->
          <div class="bg-slate-50 dark:bg-slate-800 rounded-md p-3 mb-3">
            <p class="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-line">
              {{ formatTemplatePreview(template) }}
            </p>
            
            <!-- Multimedia indicator -->
            <div v-if="hasMultimedia(template)" class="mt-2 pt-2 border-t border-slate-200 dark:border-slate-700">
              <p class="text-xs text-blue-600 dark:text-blue-400 flex items-center gap-1">
                <Icon icon="i-lucide-image" size="14" class="flex-shrink-0" />
                <span>Plantilla con contenido multimedia</span>
              </p>
            </div>
          </div>

          <!-- Variables Info -->
          <div v-if="template.variables && template.variables.length > 0" class="flex flex-wrap gap-1">
            <span
              v-for="(variable, index) in template.variables"
              :key="index"
              class="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300"
            >
              {{ formatVariablePlaceholder(index + 1) }}
            </span>
          </div>
          <p v-else class="text-xs text-slate-500 dark:text-slate-400">
            Sin variables
          </p>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="filteredTemplates.length === 0" class="text-center py-12">
        <Icon icon="i-lucide-file-text" class="text-slate-400 mb-3" size="48" />
        <h6 class="font-medium text-slate-900 dark:text-white mb-2">
          No se encontraron plantillas
        </h6>
        <p class="text-sm text-slate-600 dark:text-slate-400 mb-4">
          No hay plantillas disponibles que coincidan con tu búsqueda
        </p>
        <button
          @click="loadTemplates"
          class="px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 border border-blue-300 dark:border-blue-700 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20"
        >
          Actualizar
        </button>
      </div>
    </div>

    <!-- Selected Template Info -->
    <div v-if="selectedTemplate" class="mt-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
      <div class="flex items-start space-x-3">
        <Icon icon="i-lucide-check-circle" class="text-green-500 mt-0.5" size="20" />
        <div class="flex-1">
          <h6 class="font-medium text-green-900 dark:text-green-100">
            Plantilla seleccionada
          </h6>
          <p class="mt-1 text-sm text-green-700 dark:text-green-300">
            {{ selectedTemplate.name }} ({{ getLanguageName(selectedTemplate.language) }})
            <span v-if="selectedTemplate.variables && selectedTemplate.variables.length > 0">
              - {{ selectedTemplate.variables.length }} variable(s) requerida(s)
            </span>
          </p>
          
          <!-- Multimedia URL Input -->
          <div v-if="hasMultimedia(selectedTemplate)" class="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-800">
            <p class="text-xs font-medium text-blue-900 dark:text-blue-100 mb-2">
              {{ getMultimediaType(selectedTemplate) }} URL requerida
            </p>
            <input
              v-model="multimediaUrl"
              type="url"
              :placeholder="`Ingrese la URL del ${getMultimediaType(selectedTemplate).toLowerCase()} (https://...)`"
              class="w-full px-3 py-2 text-sm border border-blue-300 dark:border-blue-700 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              @input="emitMultimediaUrl"
            />
            <p class="mt-1 text-xs text-blue-600 dark:text-blue-400">
              Esta URL se usará para todos los mensajes del envío masivo. Debe ser de acceso público.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import InboxesAPI from 'dashboard/api/inboxes';

const props = defineProps({
  selectedTemplate: {
    type: Object,
    default: null
  },
  selectedInbox: {
    type: Object,
    default: null
  },
  multimediaUrl: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['update:selectedTemplate', 'update:multimediaUrl']);

const { t } = useI18n();
const store = useStore();

const inboxesList = useMapGetter('inboxes/getInboxes');

const isLoading = ref(false);
const isSyncing = ref(false);
const error = ref(null);
const templates = ref([]);
const searchQuery = ref('');
const languageFilter = ref('');
const multimediaUrl = ref(props.multimediaUrl || '');

// Computed properties
const availableLanguages = computed(() => {
  const languages = [...new Set(filteredTemplates.value.map(t => t.language))];
  return languages.filter(Boolean).sort();
});

const filteredTemplates = computed(() => {
  let filtered = templates.value;

  // Filter by search query
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    filtered = filtered.filter(template => 
      template.name.toLowerCase().includes(query) ||
      template.body.toLowerCase().includes(query) ||
      template.category?.toLowerCase().includes(query)
    );
  }

  // Filter by language
  if (languageFilter.value) {
    filtered = filtered.filter(template => template.language === languageFilter.value);
  }

  // Only show approved templates for bulk sending
  filtered = filtered.filter(template => template.status === 'APPROVED');

  return filtered;
});

// Methods
const loadTemplates = async () => {
  isLoading.value = true;
  error.value = null;

  try {
    if (!props.selectedInbox) {
      error.value = 'No hay un canal de WhatsApp seleccionado';
      templates.value = [];
      return;
    }

    const inbox = props.selectedInbox;
    const allTemplates = [];
    
    if (inbox.message_templates && Array.isArray(inbox.message_templates)) {
      const inboxTemplates = inbox.message_templates.map(template => {
        const processedTemplate = {
          id: template.id || `${template.name}_${inbox.id}`,
          name: template.name,
          language: template.language,
          status: template.status,
          body: getTemplateBody(template),
          variables: extractVariables(template),
          category: template.category,
          inbox_id: inbox.id,
          inbox_name: inbox.name,
          rawTemplate: template // Keep raw template to check for multimedia
        };
        
        return processedTemplate;
      });
      
      allTemplates.push(...inboxTemplates);
    }

    templates.value = allTemplates;

    if (templates.value.length === 0) {
      error.value = 'No se encontraron plantillas aprobadas en este canal';
    }

  } catch (err) {
    error.value = 'Error al cargar las plantillas de WhatsApp';
    console.error('Error loading WhatsApp templates:', err);
  } finally {
    isLoading.value = false;
  }
};

const syncTemplates = async () => {
  if (!props.selectedInbox) return;
  
  isSyncing.value = true;
  error.value = null;

  try {
    await InboxesAPI.syncWhatsAppTemplates(props.selectedInbox.id);
    
    // Wait a moment for the sync to complete, then reload
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Reload the inbox to get updated templates
    await store.dispatch('inboxes/get');
    
    // Reload templates
    await loadTemplates();
    
    // Show success message
    useAlert('Plantillas actualizadas correctamente');
  } catch (err) {
    error.value = 'Error al sincronizar plantillas. Intenta nuevamente.';
    console.error('Error syncing WhatsApp templates:', err);
  } finally {
    isSyncing.value = false;
  }
};


// Helper function to get template body from different WhatsApp providers
const getTemplateBody = (template) => {
  // WhatsApp Cloud API format
  if (template.components) {
    const bodyComponent = template.components.find(comp => comp.type === 'BODY');
    return bodyComponent?.text || template.name;
  }
  
  // 360Dialog format
  if (template.content && template.content.body) {
    return template.content.body;
  }
  
  // Fallback
  return template.body || template.text || template.name;
};

// Helper function to extract variables from template
const extractVariables = (template) => {
  let body = getTemplateBody(template);
  
  // Extract {{1}}, {{2}}, etc. placeholders
  const matches = body.match(/\{\{\d+\}\}/g);
  if (!matches) return [];
  
  // Return array with variable count
  const variableCount = Math.max(...matches.map(match => 
    parseInt(match.replace(/[{}]/g, ''))
  ));
  
  return Array.from({ length: variableCount }, (_, i) => `variable_${i + 1}`);
};

const selectTemplate = (template) => {
  emit('update:selectedTemplate', template);
};

const formatTemplatePreview = (template) => {
  // Show variables in their position ({{1}}, {{2}}, ...) instead of
  // filling them with Meta's standard example values.
  return template.body;
};

const getLanguageName = (code) => {
  const languages = {
    'es': 'Español',
    'en': 'English',
    'pt': 'Português',
    'fr': 'Français'
  };
  
  return languages[code] || code.toUpperCase();
};

const formatVariablePlaceholder = (number) => {
  return `{{${number}}}`;
};

// Helper function to check if template has multimedia components
const hasMultimedia = (template) => {
  // Check in rawTemplate (processed templates)
  const componentsToCheck = template.rawTemplate?.components || template.components;
  
  if (!componentsToCheck) return false;
  
  return componentsToCheck.some(comp => {
    return comp.type === 'HEADER' && (
      comp.format === 'IMAGE' || 
      comp.format === 'VIDEO' || 
      comp.format === 'DOCUMENT'
    );
  });
};

// Get multimedia type from template
const getMultimediaType = (template) => {
  const componentsToCheck = template.rawTemplate?.components || template.components;
  
  if (!componentsToCheck) return null;
  
  const headerComp = componentsToCheck.find(comp => comp.type === 'HEADER');
  return headerComp?.format || null;
};

// Emit multimedia URL when it changes
const emitMultimediaUrl = () => {
  emit('update:multimediaUrl', multimediaUrl.value);
};

onMounted(async () => {
  // Ensure inboxes are loaded before loading templates
  if (inboxesList.value.length === 0) {
    await store.dispatch('inboxes/get');
  }
  if (props.selectedInbox) {
    loadTemplates();
  }
});

// Watch for inbox changes
watch(() => props.selectedInbox, (newInbox) => {
  if (newInbox) {
    loadTemplates();
  } else {
    templates.value = [];
  }
});

// Watch for multimediaUrl prop changes
watch(() => props.multimediaUrl, (newUrl) => {
  multimediaUrl.value = newUrl;
});

// Clear multimedia URL when template changes
watch(() => props.selectedTemplate, (newTemplate, oldTemplate) => {
  // Only clear if template actually changed (not just selection)
  if (newTemplate?.id !== oldTemplate?.id) {
    multimediaUrl.value = '';
    emit('update:multimediaUrl', '');
  }
});
</script>